// @flow
import React from "react";
import { noop } from "lodash";
import formatCurrency from "@tvg/formatter/currency";
import pluralize from "@tvg/utils/generalUtils";
import BetCancelNotification from "../../_molecule/BetNotification";
import buildColor from "../../_static/ColorPalette";
import { infoOutline, warning } from "../../_static/Icons/iconsDuotone";
import CustomKeyboard from "../../_molecule/CustomKeyboard";
import {
  RepeatBetDetails,
  RepeatCounter,
  RepeatBetsContainer,
  RepeatButtonWrapper,
  SetToMaxWrapper
} from "./styled-components";
import Tag from "../../_atom/Tag";
import Button from "../../_atom/Buttons/button";
import { refresh, warningOutline } from "../../_static/Icons/icons";
import Icon from "../../_static/Icons";
import { fontMedium } from "../../_static/Typography";
import type { Props } from "./types";

const renderNotification = (
  notificationText: string,
  maxCounter: number,
  showWarningStyle: boolean
) => {
  const colors = !showWarningStyle
    ? {
        color: buildColor("blue_accent", "600"),
        colorBackground: buildColor("blue_accent", "000"),
        colorBorder: buildColor("blue", "100"),
        colorList: [
          buildColor("blue_accent", "200"),
          buildColor("blue_accent", "600")
        ],
        iconElement: infoOutline
      }
    : {
        color: buildColor("orange", "700"),
        colorBackground: buildColor("orange", "000"),
        colorBorder: buildColor("blue", "100"),
        iconColor: buildColor("orange", "200"),
        strokeColor: buildColor("orange", "700"),
        iconElement: warning,
        viewBoxSize: 16
      };

  return (
    <BetCancelNotification
      qaLabel="repeat-bet-notification"
      {...colors}
      title={notificationText}
      titleIsBold={false}
      textFontSize="large"
    />
  );
};

export const RepeatBets = ({
  counter,
  betAmount,
  maxCounter,
  maxCounterByAmount,
  onRepeatBets,
  onChangeValue,
  onSetToMax,
  isMaxCounterExceeded,
  isBalanceExceeded,
  notificationText
}: Props) => {
  const hasCounter = !!counter;
  return (
    <RepeatBetsContainer data-qa-label="repeat-bets">
      {renderNotification(
        notificationText,
        maxCounterByAmount,
        !isBalanceExceeded && isMaxCounterExceeded
      )}
      <RepeatBetDetails>
        <RepeatCounter
          isDisabled={!hasCounter}
          showWarning={isMaxCounterExceeded || isBalanceExceeded}
          data-qa-label="repeat-bets-counter"
        >
          {counter}
        </RepeatCounter>
        <Tag
          name={
            isMaxCounterExceeded
              ? `${maxCounter} repeats exceeded`
              : `${formatCurrency(betAmount * counter)} Ticket total`
          }
          uppercase
          tagType={
            isMaxCounterExceeded || isBalanceExceeded
              ? "RunnerFlag"
              : "RepeatBet"
          }
          isCondensed
          textSize={14}
          icon={
            isMaxCounterExceeded || isBalanceExceeded ? warningOutline : null
          }
          qaLabel="repeat-bets-notification"
        />
        <SetToMaxWrapper data-qa-label="repeat-bets-set-to-max-wrapper">
          <Button
            onClick={() => {
              onSetToMax(
                maxCounter < maxCounterByAmount
                  ? maxCounter
                  : maxCounterByAmount
              );
            }}
            type="tertiary"
            size="small"
            isUppercase={false}
            isBold={false}
            isDisabled={maxCounter === counter}
            qaLabel="repeat-bets-set-to-max"
          >
            <Icon
              icon={refresh}
              size={14}
              color={buildColor("blue_accent", "500")}
              qaLabel="repeat-bets-set-to-max-icon"
            />
            Set to max
          </Button>
        </SetToMaxWrapper>
      </RepeatBetDetails>
      <CustomKeyboard
        showMoneyPills={false}
        showPeriod={false}
        onChangeValue={(keyPressed) => {
          const newCounter = +`${counter}${keyPressed}`;
          onChangeValue(newCounter);
        }}
        onDelete={() => {
          const newCounter = +`${counter}`.slice(0, -1);
          onChangeValue(newCounter);
        }}
        isNumbersDisabled={isMaxCounterExceeded}
      />
      <RepeatButtonWrapper data-qa-label="repeat-bets-confirm-wrapper">
        <Button
          isStretched
          isUppercase={false}
          size="bigger"
          onClick={() => onRepeatBets(counter)}
          isDisabled={isMaxCounterExceeded || !hasCounter}
          qaLabel="repeat-bets-confirm"
          fontFamily={fontMedium}
        >
          Set{" "}
          {pluralize(hasCounter ? counter : 2, "repeat", hasCounter, "s", "x")}
        </Button>
      </RepeatButtonWrapper>
    </RepeatBetsContainer>
  );
};

RepeatBets.defaultProps = {
  counter: 0,
  betAmount: 0,
  maxCounter: 25,
  maxCounterByAmount: 25,
  onRepeatBets: noop,
  onChangeValue: noop,
  onSetToMax: noop,
  isMaxCounterExceeded: false,
  isBalanceExceeded: false,
  notificationText: ""
};

export default RepeatBets;
